import { Tag, Typography, theme, Button } from "antd";
import React, { useState, useRef, useEffect } from "react";
import { RecordsAnalyzedTag } from "./RecordsAnalyzedTag";
import {
  BulbOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  MonitorOutlined,
} from "@ant-design/icons";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "../../../components/topicfeed.module.css";
import { BulbIcon } from "src/components/BulbIcon";
import { stripMarkdown } from "../cardUtils";

export interface CardHeaderProps {
  type: "insight" | "summary" | "activity" | "attention" | "stats";
  title: string;
  tag: string;
  timeframe?: string;
  processedRecords?: number;
  totalRecords?: number;
  variant?: "default" | "modern" | "compact" | "bold" | "legacy";
  description?: string;
  showDescription?: boolean;
  cta?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

//TODO add variant settings like to clamp or not to clamp

// Helper to get icon based on card type
function getIcon(type: CardHeaderProps["type"]) {
  switch (type) {
    case "summary": {
      return <FileTextOutlined />;
    }
    case "insight": {
      return <BulbOutlined />;
    }
    case "activity": {
      return <MonitorOutlined />;
    }
    case "attention": {
      return <ExclamationCircleOutlined />;
    }
    default: {
      return <InfoCircleOutlined />;
    }
  }
}

// Helper to generate dynamic pill based on tag/card type
function getTagPill(
  tag: React.ReactNode,
  type: CardHeaderProps["type"],
  icon: boolean = true
) {
  // You can expand these rules as needed
  if (typeof tag === "string") {
    if (type === "summary") {
      return (
        <Tag
          color="var(--color-pill-blue)"
          icon={icon ? getIcon(type) : undefined}
          style={{
            color: "var(--color-pill-blue-accent)",
            borderColor: "var(--color-pill-blue-accent)",
          }}
        >
          {tag}
        </Tag>
      );
    }
    if (type === "insight") {
      return (
        <Tag
          color="var(--color-pill-purple)"
          icon={icon ? getIcon(type) : undefined}
          style={{
            color: "var(--color-pill-purple-accent)",
            borderColor: "var(--color-pill-purple-accent)",
          }}
        >
          {tag}
        </Tag>
      );
    }
    if (type === "activity") {
      return (
        <Tag
          color="var(--color-pill-green)"
          icon={icon ? getIcon(type) : undefined}
          style={{
            color: "var(--color-pill-green-accent)",
            borderColor: "var(--color-pill-green-accent)",
          }}
        >
          {tag}
        </Tag>
      );
    }
    if (type === "attention") {
      return (
        <Tag
          color="var(--color-pill-red)"
          icon={icon ? getIcon(type) : undefined}
          style={{
            color: "var(--color-pill-red-accent)",
            borderColor: "var(--color-pill-red-accent)",
          }}
        >
          {tag}
        </Tag>
      );
    }
    // Add more rules as needed
  }
  // Default pill
  return (
    <Tag
      color="var(--color-pill-grey)"
      icon={icon ? getIcon(type) : undefined}
      style={{
        color: "var(--color-pill-grey-text)",
        borderColor: "var(--color-pill-grey-text)",
      }}
    >
      {tag}
    </Tag>
  );
}

export const formatTimeFrame = (timeframe: string) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
      }}
    >
      <Typography.Text className="vzbl-text-2 vzbl-clamp-1-line">
        <CalendarOutlined
          style={{
            marginRight: "var(--padding-2)",
            color: "var(--color-foreground-tertiary)",
          }}
        />
        {timeframe}
      </Typography.Text>
    </div>
  );
};

export const CardHeader = ({
  type,
  title,
  tag,
  timeframe,
  processedRecords,
  totalRecords,
  variant = "default",
  description,
  showDescription = true,
  cta,
  style,
  className,
}: CardHeaderProps) => {
  const { token } = theme.useToken();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      setShowMoreButton(element.scrollHeight > element.clientHeight);
    }
  }, [description]);

  const renderDescription = (clampClass?: string) => {
    if (!showDescription || !description) return null;

    return (
      <div style={{ ...style, paddingTop: "0px" }}>
        <div ref={descriptionRef} className={`${isExpanded ? "" : clampClass}`}>
          <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml={true}
            className="vzbl-text-3 vzbl-spacing-medium"
          >
            {description}
          </Markdown>
        </div>
        {showMoreButton && (
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            style={{ padding: 0, height: "auto" }}
          >
            {isExpanded ? "Show less" : "Show more"}
          </Button>
        )}
      </div>
    );
  };

  switch (variant) {
    case "legacy": {
      return (
        <div
          className={className}
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
            gap: 8,
            ...style,
          }}
        >
          <Typography.Text
            className="vzbl-header-3"
            style={{ marginBottom: 0 }}
          >
            {typeof title === "string" ? (
              <span>{stripMarkdown(title)}</span>
            ) : (
              stripMarkdown(title)
            )}
          </Typography.Text>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 2,
              paddingLeft: token.paddingLG,
            }}
          >
            {getTagPill(tag, type)}
            {cta && getTagPill(cta, type)}
            {processedRecords && totalRecords && (
              <RecordsAnalyzedTag
                variant="text"
                processedRecords={processedRecords}
                totalRecords={totalRecords}
              />
            )}
            <span
              className="vzbl-text-2 vzbl-clamp-1-line"
              style={{
                marginTop: 2,
              }}
            >
              {timeframe}
            </span>
          </div>
        </div>
      );
    }
    case "compact": {
      return (
        <div
          className={className}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 8,
            ...style,
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <Typography.Text className="vzbl-highbrow vzbl-pill-md vzbl-pill-clear">
              <BulbIcon size={12} style={{ marginRight: "var(--padding-2)" }} />
              {tag}
            </Typography.Text>
            <Typography.Text className="vzbl-header-3">
              {typeof title === "string" ? (
                <span>{stripMarkdown(title)}</span>
              ) : (
                stripMarkdown(title)
              )}
            </Typography.Text>
            {renderDescription("vzbl-clamp-2-lines")}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 8,
              minWidth: 140,
              paddingTop: "var(--padding-2)",
              paddingLeft: "var(--padding-6)",
            }}
          >
            <div style={{ flex: 1 }}>
              {timeframe && formatTimeFrame(timeframe)}
            </div>

            {processedRecords && totalRecords && (
              <div style={{ flex: 1 }}>
                <RecordsAnalyzedTag
                  variant="text"
                  processedRecords={processedRecords}
                  totalRecords={totalRecords}
                />
              </div>
            )}
            {cta && (
              <div style={{ flex: 1 }}>{getTagPill(cta, type, false)}</div>
            )}
          </div>
        </div>
      );
    }
    case "bold": {
      return (
        <div className={className}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              gap: 8,
              ...style,
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <Typography.Text className="vzbl-highbrow">{tag}</Typography.Text>
              <Typography.Text className="vzbl-header-2">
                {typeof title === "string" ? (
                  <span>{stripMarkdown(title)}</span>
                ) : (
                  stripMarkdown(title)
                )}
              </Typography.Text>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 8,
                minWidth: 140,
                paddingTop: "var(--padding-2)",
                paddingLeft: "var(--padding-6)",
              }}
            >
              <div style={{ flex: 1 }}>
                {timeframe && formatTimeFrame(timeframe)}
              </div>

              {processedRecords && totalRecords && (
                <div style={{ flex: 1 }}>
                  <RecordsAnalyzedTag
                    variant="text"
                    processedRecords={processedRecords}
                    totalRecords={totalRecords}
                  />
                </div>
              )}
              {cta && (
                <div style={{ flex: 1 }}>{getTagPill(cta, type, false)}</div>
              )}
            </div>
          </div>
          {renderDescription("")}
        </div>
      );
    }
    default: {
      return (
        <div className={className}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              gap: 8,
              ...style,
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <Typography.Text className="vzbl-highbrow">{tag}</Typography.Text>
              <Typography.Text className="vzbl-header-3">
                {typeof title === "string" ? (
                  <span>{stripMarkdown(title)}</span>
                ) : (
                  stripMarkdown(title)
                )}
              </Typography.Text>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 4,
                minWidth: 140,
                paddingTop: "var(--padding-2)",
                paddingLeft: "var(--padding-6)",
              }}
            >
              <div style={{ flex: 1 }}>
                {timeframe && formatTimeFrame(timeframe)}
              </div>

              {processedRecords && totalRecords && (
                <div style={{ flex: 1 }}>
                  <RecordsAnalyzedTag
                    variant="text"
                    processedRecords={processedRecords}
                    totalRecords={totalRecords}
                  />
                </div>
              )}
              {cta && (
                <div style={{ flex: 1 }}>{getTagPill(cta, type, false)}</div>
              )}
            </div>
          </div>
          {renderDescription()}
        </div>
      );
    }
  }
};
